import { gql } from '@apollo/client';
import { debounce, find, forEach, isError, map, remove } from 'lodash-es';
import { v4 as uuid } from 'uuid';

import type { AuthUser } from 'common/auth/types';
import getWebAppDetails from 'common/utils/web_app_details';

import { loggerClient } from '../graphql/client';
import { getCurrentLng } from '../i18n/i18n';

import type { Logger, UserMeta } from './log.provider';

export const NATIVE_LOG_SOURCE = 'siteMobile';
export interface CIAppLog {
    timestamp: Date;
    sessionId: string;
    topic?: string | null;
    source: string;
    applicationSource: string;
    level: string;
    message: string[]; // it looks like we can send anything to the BE

    hostname: string;
    location: string;
    browser: object;
    engine: object;
    OSName: string;
    OSVersion: string;
    platform: object;
    language: string;

    userPk?: string | null;
    studyId?: string;
    subjectId?: string;
    siteId?: string;

    appVersion: string | null; // Both web and native-site | current version of the application the user is using (so something like ‘1.6.0’)
    deviceId?: string; // native-site only | serial no . of device
    deviceName?: string; //native-site only | eg. Samsung Note
}

const sendLogsGQL = gql`
    mutation SendLogs($logs: [InputCreateAppLog!]) {
        logApplicationData(inputs: $logs)
    }
`;

interface LogContext {
    topic?: string;
    studyId?: string;
    siteId?: string;
    subjectId?: string;
}

function convertLogToAPI(e: unknown[], level: string, logMeta: UserMeta, appVersion: string | null): CIAppLog {
    // Callers may pass a context object (studyId, topic, ...) alongside the message
    const contexts = e as LogContext[];
    const escapedErrors = map(e, (err) => JSON.stringify(err, isError(err) ? Object.getOwnPropertyNames(err) : null));

    let topic: string | null = null;
    if (e && e.length > 1 && contexts[1].topic) {
        topic = contexts[1].topic;
    }
    //when studyId is passed as the second parameter along with message
    // `false` (when fewer than two args) is harmless below: `false?.studyId` is undefined
    const sitestudyIdArr = (e?.length > 1 && find(contexts, 'studyId')) as LogContext | undefined;
    const studyId = sitestudyIdArr?.studyId;
    const siteId = sitestudyIdArr?.siteId;

    const subjectIdArr = (e?.length > 1 && find(contexts, 'subjectId')) as LogContext | undefined;
    const subjectId = subjectIdArr?.subjectId;

    const { userPk, sessionId, hostname, location, browser, engine, os, platform } = logMeta;

    const appLog = {
        timestamp: new Date(),
        userPk,
        sessionId: sessionId || uuid(), // TODO: BE is currently requiring sessionId, but don't have a way of identifying for non-auth users when to generate, so just generating something for now
        topic,
        source: 'web',
        applicationSource: 'admin',
        level,
        message: escapedErrors,
        /**
         * this will log (two examples), which can be parsed again:
         * "message": "[\"abc\", {\"studyId\":\"Study.3ee7ec2d-4578-4921-9f14-b52ee64c33bd\",\"message\":\"Study Info tab accessed\"}]",
         * "message": "[{\"stack\":\"Error: xxxx\\n    at _EditStudyDialog (http://localhost:3000/static/js/3.chunk.js:305:90)\\n    at renderWi ....
         */
        hostname,
        location,
        browser,
        engine,
        OSName: os?.['name'],
        OSVersion: os?.['version'],
        platform: { type: platform?.['type'], vendor: platform?.['vendor'] },
        language: getCurrentLng(),
        studyId,
        siteId,
        subjectId,
        appVersion,
    };

    return appLog;
}

const LOG_STORAGE_KEY = 'ciLogQueue';

function addLogToQueue(log: CIAppLog): void {
    // The key may be missing: JSON.parse(null) returns null, which the checks below rely on (also applies to the reads further down)
    let logStorage = JSON.parse(window.localStorage.getItem(LOG_STORAGE_KEY) as string);
    if (!logStorage) {
        logStorage = [];
    }

    logStorage.push({ id: uuid(), log: log });
    try {
        window.localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(logStorage));
    } catch (error) {
        if (error.name === 'QuotaExceededError') {
            window.localStorage.removeItem(LOG_STORAGE_KEY);
        }
    }
}

const LOG_FLUSH_FREQ = 1000; // One second

export default class CIAppLogger implements Logger {
    private gqlClient: typeof loggerClient;
    private appVersion: string | null = null;
    private lastSendPromise: Promise<void> | null = null;
    private sendLogs;

    constructor() {
        this.sendLogs = debounce((): void => {
            let logStorage = JSON.parse(window.localStorage.getItem(LOG_STORAGE_KEY) as string);
            const activeUser = localStorage.getItem('activeUser') as string;
            const userPk = (JSON.parse(activeUser) as AuthUser)?.pk;

            if (!userPk || !logStorage || !logStorage.length) {
                window.localStorage.removeItem(LOG_STORAGE_KEY);
                this.lastSendPromise = Promise.resolve();

                return;
            }

            const logs = map(logStorage, 'log');
            const logKeys = map(logStorage, 'id');

            const response = this.gqlClient.mutate({
                mutation: sendLogsGQL,
                variables: {
                    logs: logs,
                },
                context: {
                    isLoggerClient: true,
                },
            });

            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            this.lastSendPromise = Promise.resolve(response).then((): void => {
                logStorage = JSON.parse(window.localStorage.getItem(LOG_STORAGE_KEY) as string);
                forEach(logKeys, (key): void => {
                    // eslint-disable-next-line lodash/prefer-immutable-method
                    remove(logStorage, { id: key });
                });
                window.localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(logStorage));
            });
        }, LOG_FLUSH_FREQ);
    }

    public initialize(): void {
        this.appVersion = getWebAppDetails().appVersion;

        this.gqlClient = loggerClient;
        this.sendLogs();
    }

    public log(e: unknown[], logMeta: UserMeta): void {
        this.writeLog(e, 'debug', logMeta);
    }

    public info(e: unknown[], logMeta: UserMeta): void {
        this.writeLog(e, 'info', logMeta);
    }

    public warn(e: unknown[], logMeta: UserMeta): void {
        this.writeLog(e, 'warn', logMeta);
    }

    public error(e: unknown[], logMeta: UserMeta): void {
        this.writeLog(e, 'error', logMeta);
    }

    public async flush(timeoutMs: number): Promise<void> {
        this.sendLogs();
        this.sendLogs.flush();

        if (this.lastSendPromise === null) {
            return;
        }

        await Promise.race([this.lastSendPromise, new Promise<void>((resolve) => setTimeout(resolve, timeoutMs))]);
    }

    private writeLog(e: unknown[], level: string, logMeta: UserMeta): void {
        const log = convertLogToAPI(e, level, logMeta, this.appVersion);
        addLogToQueue(log);
        this.sendLogs();
    }
}
